'use client';

import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer } from '@deck.gl/layers';

export interface RoutePathData {
  /** Decoded path coordinates as [lng, lat] tuples */
  path: [number, number][];
  /** Route color in RGBA format [r, g, b, a] where values are 0-255 */
  color: [number, number, number, number];
  /** Optional unique identifier for the route */
  id?: string;
}

interface RouteLayerProps {
  /** Array of route data to render */
  routeData: RoutePathData[];
}

/**
 * RouteLayer renders route paths on the map using deck.gl PathLayer
 * with GoogleMapsOverlay for seamless integration with Google Maps.
 */
const RouteLayer = ({ routeData }: RouteLayerProps) => {
  const map = useMap();
  const deckOverlayRef = useRef<GoogleMapsOverlay | null>(null);

  // Initialize deck.gl overlay when map is available
  useEffect(() => {
    if (!map) {
      return;
    }

    const overlay = new GoogleMapsOverlay({ interleaved: false });
    overlay.setMap(map);
    deckOverlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlay.finalize();
      deckOverlayRef.current = null;
    };
  }, [map]);

  // Update path layer when route data changes
  useEffect(() => {
    if (!deckOverlayRef.current) {
      return;
    }

    if (routeData.length > 0) {
      const pathLayer = new PathLayer<RoutePathData>({
        id: 'route-path-layer',
        data: routeData,
        getPath: (d) => d.path,
        getColor: (d) => d.color,
        getWidth: 5,
        widthUnits: 'pixels',
        jointRounded: true,
        capRounded: true,
        pickable: false,
      });

      deckOverlayRef.current.setProps({ layers: [pathLayer] });
    } else {
      // Clear layers when no route data
      deckOverlayRef.current.setProps({ layers: [] });
    }
  }, [routeData]);

  return null;
};

export default RouteLayer;
